/**
 * PRODUCT BRANDS, AS A FIRST-CLASS THING — the same shape collections have.
 *
 * `ProductBrand` is salon-scoped with `name`, `translations`, `displayOrder`,
 * `isHidden`, `logoUrl`, `coverImageUrl`, `heroImageUrl` and
 * `isOfficialImporter`. The public API already hides hidden brands, so nothing
 * here filters on visibility: an absent brand is absent.
 *
 * ── THE SLUG IS CANONICAL, AND THE ID IS A COMPATIBILITY ARM ─────────────
 *
 * The server accepts either at `/product-brands/:brandIdOrSlug`, but `slug` is
 * still nullable on rows that predate the backfill. So every link is built
 * from `brandKey`, which prefers the slug and falls back to the id — one
 * function, so a surface cannot quietly link by id while another links by slug
 * and the two produce different URLs for the same brand. `لوريال` is at
 * `/lwryal`, not at a UUID.
 *
 * ── WHY THE COVER RESOLUTION IS ITS OWN LADDER ───────────────────────────
 *
 * A brand has three images and they are not interchangeable: `heroImageUrl` is
 * the wide banner a brand PAGE shows, `coverImageUrl` is the square a brand
 * CARD shows, and `logoUrl` is the mark the strip and the drawer show. Each
 * falls back down the ladder rather than to a placeholder, because a brand
 * with only a logo must still render as a card — that is the ordinary case for
 * a business that has just added one.
 *
 * There is a ladder per SHAPE, and there are three shapes, so there are three
 * ladders. The mark's was missing until the owner asked for it: "see where you
 * can set the brand pic, so it's also shown in the product page and in the
 * main page carousel." Both of those slots are mark slots, and both read
 * `logoUrl` and stopped — so an owner who uploaded a cover and no mark set a
 * picture that appeared on neither surface no matter what they did.
 */
import { getDisplayNameForLocale } from './displayNameForLocale';

/** The path segment for a brand: the slug when it has one, the id otherwise. */
export function brandKey(brand) {
  if (!brand) return null;
  const slug = typeof brand.slug === 'string' ? brand.slug.trim() : '';
  if (slug) return slug;
  return brand.id ?? null;
}

/** `/:salonSlug/shop/brands/:key`, built from one place. */
export function brandPath(basePath, brand) {
  const key = brandKey(brand);
  if (!key) return null;
  return `${basePath || ''}/shop/brands/${key}`;
}

/** The brand's name in the reader's language. `لوريال` in Arabic, `L'Oréal` in English. */
export function brandName(brand, language) {
  return getDisplayNameForLocale(brand, language);
}

/** The square a brand CARD shows: its own cover, then its hero, then its mark. */
export function resolveBrandCoverUrl(brand) {
  if (!brand) return null;
  return brand.coverImageUrl || brand.heroImageUrl || brand.logoUrl || null;
}

/**
 * THE MARK SLOT: the strip on the main page, and the row on a product page.
 *
 * Logo first, because a row of logos is asking for logos — and then down,
 * because a picture the owner uploaded beats a letter tile and both callers
 * letterbox with `object-fit: contain` at one block-size, so a square cover or
 * a wide banner arrives whole rather than cropped into a disc.
 *
 * An empty string is not a URL. `logoUrl: '   '` is what a cleared field
 * arrives as, and an `<img>` with a blank `src` is a broken-image glyph — the
 * one thing neither surface may render. Blank rungs are skipped and a brand
 * with nothing set answers `null`, which is the caller's cue to print the
 * brand's NAME instead.
 */
export function resolveBrandMarkUrl(brand) {
  if (!brand) return null;
  const rungs = [brand.logoUrl, brand.coverImageUrl, brand.heroImageUrl];
  for (const url of rungs) {
    if (typeof url === 'string' && url.trim()) return url;
  }
  return null;
}

/**
 * The brand's LOGO and nothing else — for the יבואן רשמי marquee, a row of
 * wordmarks where a fallen-through campaign photo would be the odd one out.
 * `null` when there is no logo; the marquee leaves such a brand off rather
 * than drawing its name among the marks.
 */
export function resolveBrandLogoUrl(brand) {
  if (!brand) return null;
  const url = brand.logoUrl;
  return typeof url === 'string' && url.trim() ? url : null;
}

/**
 * A PHOTO CARD: the brand's campaign photograph, and its logo to set on it.
 *
 * dk-store's מותגים מובילים is a row of photographs with each brand's wordmark
 * laid over the foot of its picture, and the owner asked for that by name. It
 * needs BOTH pictures: a lone logo shown as a photo would be cropped, and a
 * lone photo has no mark to identify it. So this answers `null` unless the
 * brand has a logo AND a cover or hero — and every other brand keeps going
 * through `resolveBrandMarkUrl`, unchanged.
 */
export function resolveBrandPhotoCard(brand) {
  const logo = resolveBrandLogoUrl(brand);
  if (!logo) return null;
  const photo = [brand.coverImageUrl, brand.heroImageUrl]
    .find((u) => typeof u === 'string' && u.trim());
  return photo ? { photo, logo } : null;
}

/** The wide banner a brand PAGE shows: its own hero, then its cover, then its mark. */
export function resolveBrandHeroUrl(brand) {
  if (!brand) return null;
  return brand.heroImageUrl || brand.coverImageUrl || null;
}

/** In `displayOrder`, then by name — the order the API already sends, made explicit. */
export function orderBrands(brands) {
  return [...(Array.isArray(brands) ? brands : [])].sort((a, b) => {
    const ao = Number.isFinite(Number(a?.displayOrder)) ? Number(a.displayOrder) : 0;
    const bo = Number.isFinite(Number(b?.displayOrder)) ? Number(b.displayOrder) : 0;
    if (ao !== bo) return ao - bo;
    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
  });
}

/**
 * "מותגים מובילים" is a row of PHOTOGRAPHS. When a shop has at least
 * `LEADING_ROW_MIN_PHOTOS` brands with a photo card, the row shows those and
 * only those, in the owner's order — a lettered plate or a lone logo between
 * campaign photographs is the odd card out (dk-store's own carousel shows only
 * its photographed brands). Below that, every brand stays, as it always has.
 * A brand left out is still reachable: the logo marquee, the side pane's brand
 * list and its own brand page. See
 * `Pages/Shop/__tests__/theLeadingBrandsRowIsARowOfPhotographs.test.js`.
 */
export const LEADING_ROW_MIN_PHOTOS = 3;

export function leadingBrandsForRow(brands) {
  const ordered = orderBrands(brands);
  const photographed = ordered.filter((b) => resolveBrandPhotoCard(b));
  return photographed.length >= LEADING_ROW_MIN_PHOTOS ? photographed : ordered;
}

/** The brands this business is the official importer of. */
export function officialImporterBrands(brands) {
  return orderBrands(brands).filter((b) => b?.isOfficialImporter === true);
}

/**
 * THE BRANDS THAT ACTUALLY APPEAR IN A SET OF PRODUCTS — backlog item 30.
 *
 * A collection's payload does not carry a brand list; each PRODUCT carries its
 * own `brand`. Deduped by id (a brand with three products in the collection is
 * one card), ordered by the brand's own `displayOrder` so the row reads the
 * same here as it does on the shop's main page, and carrying a `productCount`
 * counted WITHIN this set — a brand card under "Summer picks" that claimed the
 * brand's whole catalogue would be saying something untrue about the page it
 * is on.
 */
export function brandsInProducts(products) {
  const byId = new Map();
  for (const p of Array.isArray(products) ? products : []) {
    const brand = p?.brand;
    if (!brand?.id) continue;
    if (brand.isHidden === true) continue;
    const seen = byId.get(brand.id);
    if (seen) { seen.productCount += 1; continue; }
    byId.set(brand.id, { ...brand, productCount: 1 });
  }
  return orderBrands([...byId.values()]);
}

/**
 * The names to print in "official importer of X" — translated, deduped,
 * and empty when there is nothing to name, so the caller renders nothing
 * rather than a sentence with a hole in it.
 */
export function officialImporterNames(brands, language) {
  const names = officialImporterBrands(brands)
    .map((b) => brandName(b, language))
    .map((n) => (n || '').trim())
    .filter(Boolean);
  return [...new Set(names)];
}

/**
 * "لوريال" / "لوريال و‏لوريال إسرائيل" — a list a person reads, in their own
 * language's conjunction. `Intl.ListFormat` where the engine has it (every
 * browser this storefront supports, and node 16+), and a comma-joined
 * fallback where it does not, so a missing Intl never blanks the claim.
 */
export function joinBrandNames(names, language, andWord) {
  const list = (names || []).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.ListFormat === 'function') {
      return new Intl.ListFormat(language || 'en', { style: 'long', type: 'conjunction' }).format(list);
    }
  } catch (_) { /* fall through */ }
  const head = list.slice(0, -1).join(', ');
  return `${head} ${andWord || '&'} ${list[list.length - 1]}`;
}
